import type { ItemType, ProductType } from "@jasper/client-models";
import type {
  UpdateProductArgs,
  useOrderConfiguration,
} from "@jasper/use-cases";
import type { BulkProductGroup } from "../bulkProductGroup";
import type { ToothFormat } from "@jasper/forms";

export type OrderConfiguration = ReturnType<typeof useOrderConfiguration>;

export type ConfigurableProduct = NonNullable<
  NonNullable<OrderConfiguration["order"]>["products"]
>[number];

/** What a step needs to know about the group it configures. */
export type StepBulkGroup = Pick<
  BulkProductGroup<ConfigurableProduct>,
  "isBulkMode" | "groupProducts" | "groupTeeth"
>;

export type BulkTeethMutationStrategy = {
  getTargetProductType: (teeth: number[]) => ProductType;
  canReuseProduct?: (args: {
    currentProductType: ProductType;
    targetProductType: ProductType;
  }) => boolean;
};

export type BulkTeethSubmitOptions = {
  mutationStrategy?: BulkTeethMutationStrategy;
};

export type StepSaveState = {
  isDirty: boolean;
  isInvalid: boolean;
  isSaving: boolean;
  isValidating: boolean;
  flush: () => Promise<void>;
  validateAllFields: () => Promise<void>;
  stepKey: StepKeys;
};

export type RegisterStepSave = (
  stepKey: StepKeys,
  getState: () => StepSaveState
) => () => void;

type TeethStepBulk = {
  isSupported: boolean;
  preselectedTeeth?: ReadonlyArray<number>;
  submissionOptions?: BulkTeethSubmitOptions;
  onSubmit: <T extends ToothFormat>(
    teeth: T[][],
    formatTeethSelection: (args: {
      teeth: T[];
      product: ConfigurableProduct;
      itemType: ItemType;
    }) => Promise<UpdateProductArgs> | UpdateProductArgs
  ) => Promise<void>;
};

/**
 * props shared by every product configuration step.
 *
 * Steps are host-agnostic views: navigation (`onNext`/`onBack`) and the loaded
 * product/order configuration are injected by the host (webapp multi-product
 * flow via useOrderWorkflow, labs single-product flow via useProductWorkflow).
 *
 */
export type ProductStepProps = {
  orderId: string;
  productId: string;
  stepKey?: StepKeys;
  itemType: ItemType;
  product: ConfigurableProduct;
  orderConfiguration: OrderConfiguration;
  registerStepSave?: RegisterStepSave;
  bulkGroup?: StepBulkGroup;
  bulk?: TeethStepBulk;
  goToStep?: () => void;
};

export type StepKeys =
  | "material"
  | "inlayCore"
  | "preferences"
  | "adjacentTeeth"
  | "implantBridgeTeeth"
  | "teeth"
  | "tooth"
  | "crownTeeth"
  | "arch"
  | "waxUpType"
  | "implantConnection"
  | "abutment"
  | "implantRetention"
  | "inlayCoreCrown"
  | "splintType";

export const StepKeysEnum = {
  adjacentTeeth: "adjacentTeeth",
  teeth: "teeth",
  tooth: "tooth",
  crownTeeth: "crownTeeth",
  implantBridgeTeeth: "implantBridgeTeeth",
  arch: "arch",
  material: "material",
  inlayCore: "inlayCore",
  preferences: "preferences",
  waxUpType: "waxUpType",
  implantConnection: "implantConnection",
  abutment: "abutment",
  implantRetention: "implantRetention",
  inlayCoreCrown: "inlayCoreCrown",
  splintType: "splintType",
} as const satisfies Record<string, StepKeys>;
